import {
  BlocksViewLink,
  BlocksViewNode,
  BlocksViewRenderingData,
  BlocksViewSourceDataShape,
} from '../types';
import {
  getComponentTypesInfo,
  getGraphNodeCount,
  isComponentGraphEdge,
} from '@ardoq/graph';
import { createNode } from './createNode';
import { createLink } from './createLink';
import { reduceNodes, BlocksViewReducerState } from './reduceNodes';
import { graphEdgesToReferenceTypes } from 'tabview/relationshipDiagrams/graphEdgesToReferenceTypes';
import {
  hasDefaultGreyColorComponentFilter,
  hasDefaultGreyColorReferenceFilter,
  maybeGrayOutTypes,
} from 'models/utils/filterUtils';
import { NODE_DEFAULTS } from '../consts';
import {
  augmentNodeWithLinks,
  buildNodeLinksMap,
  partitionGraphItemsHierarchy,
} from 'tabview/relationshipDiagrams/viewModel$/utils';
import { ROOT_NODE_ID } from 'tabview/relationshipDiagrams/consts';
import { setDescendantCounts } from './util';
import type { Locale } from '@ardoq/locale';
import { arrangedLayoutFor } from '../layout/arrangementLayout';
import { indexTreeLinks, withTreeParent } from '../layout/treeLinks';

const buildBlocksViewRenderingData = (
  sourceDataShape: BlocksViewSourceDataShape,
  locale: Locale
): BlocksViewRenderingData => {
  const {
    groups: graphGroups,
    nodes: graphNodes,
    edges: graphEdges,
    errors,
    hasClones,
    viewInterface,
    viewSettings,
    viewInstanceId,
    performanceTrackingId,
    context,
    activeConditionalFormattingForLegend,
    formattingFilters,
    loadedGraph,
  } = sourceDataShape;

  const contextComponentId = context.componentId;

  const {
    rootGroups,
    rootGraphNodes,
    componentIds,
    groupChildren,
    allGraphEdges,
  } = partitionGraphItemsHierarchy({ graphGroups, graphNodes, graphEdges });

  const rootNode =
    rootGroups.length + rootGraphNodes.length
      ? ({
          ...NODE_DEFAULTS,
          id: ROOT_NODE_ID,
          parent: null,
          children: [],
        } satisfies BlocksViewNode)
      : null;

  const sharedReduceState = {
    groupChildren,
    viewInstanceId,
    createNode,
    contextComponentId,
  };

  const { map: groupNodeMap } = rootGroups.reduce<BlocksViewReducerState>(
    reduceNodes,
    { map: new Map(), parent: rootNode, ...sharedReduceState }
  );

  const { map: leafNodeMap } = rootGraphNodes.reduce<BlocksViewReducerState>(
    reduceNodes,
    { map: new Map(), parent: rootNode, ...sharedReduceState }
  );

  const nodeMap = new Map([...groupNodeMap, ...leafNodeMap]);

  if (rootNode) {
    setDescendantCounts(rootNode);
  }

  // Apply grey color to component and reference types when "Keep original color" is unchecked
  // (Blocks view always runs in viewpoint mode)
  const shouldApplyGreyToComponents =
    hasDefaultGreyColorComponentFilter(formattingFilters);
  const shouldApplyGreyToReferences =
    hasDefaultGreyColorReferenceFilter(formattingFilters);

  const { referenceTypes: allEdgesReferenceTypes, legendReferenceTypes } =
    graphEdgesToReferenceTypes(
      viewInterface,
      allGraphEdges,
      shouldApplyGreyToReferences
    );

  const arrangedLayout = arrangedLayoutFor({
    root: rootNode,
    loadedGraph,
    viewSettings,
    locale,
  });

  const parentByChild = indexTreeLinks(arrangedLayout?.treeLinks ?? []);
  const links = allGraphEdges.map(graphEdge => {
    const link = createLink(
      viewInterface,
      graphEdge,
      nodeMap,
      allEdgesReferenceTypes.get(graphEdge.modelId) ?? null
    );
    return isComponentGraphEdge(graphEdge)
      ? link
      : withTreeParent(parentByChild, link);
  });

  const nodeLinks = buildNodeLinksMap<BlocksViewNode, BlocksViewLink>(links);

  if (rootNode) {
    augmentNodeWithLinks(rootNode, nodeLinks);
  }

  const { legendComponentTypes: legendComponentTypeBase } =
    getComponentTypesInfo(componentIds, {
      enhancedScopeData: loadedGraph.scopeData,
      formattingFilters,
    });
  const legendComponentTypes = maybeGrayOutTypes(
    legendComponentTypeBase,
    shouldApplyGreyToComponents,
    'borderColor'
  );

  return {
    context,
    viewSettings,
    graph: rootNode ? { root: rootNode, edges: links } : null,
    componentTypes: legendComponentTypes,
    referenceModelTypes: legendReferenceTypes,
    errors,
    hasClones,
    groups: graphGroups,
    isViewEmpty: !getGraphNodeCount(sourceDataShape),
    viewInstanceId,
    performanceTrackingId,
    activeConditionalFormattingForLegend,
    arrangedLayout,
  };
};

export { buildBlocksViewRenderingData };
